import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import GeneralForm from '../../components/generalForm';
import { useBusinessProfile, useBusinessProfileEdit, useServiceAreas } from './apiHooks';
import utils from '../../utils/utils';
import GooglePlacesField from '../../components/customFields/googlePlacesField';
import ContactDetailsField from '../../components/customFields/contactDetails';
import CustomSelect from '../../components/customFields/customSelect';

function BusinessProfileForm({ intl, match, history }) {
  const [formBusinessProfile, setFormBusinessProfile] = useState({});

  const {
    isLoading: loading,
    isError,
    error,
  } = useBusinessProfile({
    businessProfileId: match.params.businessProfileId,
    onSuccess: (businessProfile) =>
      setFormBusinessProfile({
        ...businessProfile,
        invoiceEmails: businessProfile.invoiceEmails.join(', '),
        transactionalEmails: businessProfile.transactionalEmails.join(', '),
        promotionEmails: businessProfile.promotionEmails.join(', '),
        differentDeliveryAddress: !!businessProfile.deliveryAddress,
      }),
  });

  const {
    editBusinessProfile,
    isLoading: editLoading,
    isError: isEditError,
    error: businessProfileEditError,
  } = useBusinessProfileEdit(() =>
    history.push({
      pathname: '/admin/businessProfiles',
    })
  );

  const invoiceIntervalOptions = ['WEEKLY', 'BI_WEEKLY', 'MONTHLY'].map((interval) => {
    return {
      label: intl.formatMessage({ id: `business.profile.invoice.interval.${interval}` }),
      value: interval,
    };
  });

  const { serviceAreas } = useServiceAreas();

  const serviceAreaOptions = serviceAreas
    ? serviceAreas.map((area) => {
        return {
          value: area.id,
          label: `${area.name}`,
        };
      })
    : [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      companyName: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.company.name.label' }),
      },
      registryCode: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.registry.code.label' }),
      },
      vatNumber: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.vat.number.label' }),
      },
      invoiceInterval: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.invoice.interval.label' }),
        options: invoiceIntervalOptions,
        showClearOption: true,
      },
      contactDetails: {
        type: 'object',
        default: {
          name: '',
          email: '',
          phoneNumber: '',
        },
      },
      transactionalEmails: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.transactional.emails.label' }),
      },
      invoiceEmails: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.invoice.emails.label' }),
      },
      promotionEmails: {
        type: 'string',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.promotion.emails.label' }),
      },
      address: {
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.address.label' }),
        type: 'object',
        required: [],
        properties: {
          serviceAreaId: {
            type: 'string',
            options: serviceAreaOptions,
          },
          unit: {
            type: 'string',
            default: '',
          },
          street: {
            type: 'string',
            default: '',
          },
          city: {
            type: 'string',
            default: '',
          },
          country: {
            type: 'string',
            default: '',
          },
          postCode: {
            type: 'string',
            default: '',
          },
          coordinates: {
            type: 'object',
            required: [],
            properties: {
              lat: {
                type: 'string',
              },
              lon: {
                type: 'string',
              },
            },
          },
        },
      },
      differentDeliveryAddress: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'admin.business.profile.form.field.different.delivery.address.label' }),
      },
    },
  };

  if (formBusinessProfile.differentDeliveryAddress) {
    formSchema.properties.deliveryAddress = {
      title: intl.formatMessage({ id: 'admin.business.profile.form.field.delivery.address.label' }),
      type: 'object',
      required: [],
      properties: {
        serviceAreaId: {
          type: 'string',
          options: serviceAreaOptions,
        },
        unit: {
          type: 'string',
          default: '',
        },
        street: {
          type: 'string',
          default: '',
        },
        city: {
          type: 'string',
          default: '',
        },
        country: {
          type: 'string',
          default: '',
        },
        postCode: {
          type: 'string',
          default: '',
        },
        coordinates: {
          type: 'object',
          required: [],
          properties: {
            lat: {
              type: 'string',
            },
            lon: {
              type: 'string',
            },
          },
        },
      },
    };
  }

  const uiSchema = {
    address: {
      'ui:field': 'googlePlacesField',
    },
    deliveryAddress: {
      'ui:field': 'googlePlacesField',
    },
    contactDetails: {
      'ui:field': 'contactDetailsField',
    },
    invoiceInterval: {
      'ui:field': 'customSelect',
    },
    transactionalEmails: {
      'ui:help': intl.formatMessage({ id: 'admin.business.profile.form.field.transactional.emails.help' }),
    },
    invoiceEmails: {
      'ui:help': intl.formatMessage({ id: 'admin.business.profile.form.field.invoice.emails.help' }),
    },
    promotionEmails: {
      'ui:help': intl.formatMessage({ id: 'admin.business.profile.form.field.promotion.emails.help' }),
    },
  };

  const onFormSubmit = () => {
    try {
      editBusinessProfile({ businessProfile: formBusinessProfile });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  const formFields = {
    googlePlacesField: GooglePlacesField,
    contactDetailsField: ContactDetailsField,
    customSelect: CustomSelect,
  };

  const buttonCopy = 'business.profile.edit.form.submit';

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 admin__form">
        <GeneralForm
          onChange={(event) => setFormBusinessProfile(event.formData)}
          onSubmit={onFormSubmit}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={formBusinessProfile}
          buttonCopy={buttonCopy}
          errorMessage={
            isError || isEditError ? utils.getErrorMessageFromResponse(error || businessProfileEditError, true) : null
          }
          loading={loading || editLoading}
          hideImageUpload={true}
        />
      </div>
    </div>
  );
}

BusinessProfileForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

BusinessProfileForm.defaultProps = {
  onSuccess: () => {},
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(BusinessProfileForm));
